import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { type SuggestedWorkflowExecutionResult } from '../../../../../../models/SuggestedWorkflow'
import { useExecuteAiAgentMutation } from '../../../../../../store/ai/aiAgent'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useGetAdditionalObjectsQuery } from '../../../../../../store/integrations/integrations'
import { workflowListsRequest } from '../../../../../../store/integrations/workflowLists'
import { TrackEvent, useTracking } from '../../../common/tracking'
import { type HandledSuggestions, type IntegrationWorkflowLists, type IntegrationWorkflowTarget, selectSuggestions } from './integrationWorkflow'
import { getIntegrationWorkflowAdapter } from './integrationWorkflowAdapters'

type IntegrationWorkflowsLoadStatus = 'unlinked' | 'loading' | 'error' | 'ready'

export interface UseIntegrationWorkflowsParams {
  target: IntegrationWorkflowTarget | null
  roomId: string
  sessionId: string
}

const EMPTY_LISTS: IntegrationWorkflowLists = { running: [], completed: [], templates: [] }
const NO_HANDLED_SUGGESTIONS: HandledSuggestions = { workflowIds: [], templateIds: [] }

export const useIntegrationWorkflows = ({ target, roomId, sessionId }: UseIntegrationWorkflowsParams) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const [executeAiAgent] = useExecuteAiAgentMutation()
  const [suggestionResult, setSuggestionResult] = useState<SuggestedWorkflowExecutionResult | null>(null)
  const [isSuggesting, setSuggesting] = useState(false)
  const [handledSuggestions, setHandledSuggestions] = useState<HandledSuggestions>(NO_HANDLED_SUGGESTIONS)
  const latestSuggestionRequest = useRef(0)

  const queryArgs = target ? workflowListsRequest({ accountId, connector: target.serviceId, externalId: target.externalId }) : skipToken
  const { currentData: data, isError, refetch } = useGetAdditionalObjectsQuery(queryArgs)

  const lists = useMemo(() => (target && data ? getIntegrationWorkflowAdapter(target.serviceId).toLists(data) : EMPTY_LISTS), [data, target])

  const status: IntegrationWorkflowsLoadStatus = !target ? 'unlinked' : data ? 'ready' : isError ? 'error' : 'loading'

  const runSuggestions = useCallback(async () => {
    if (!target) {
      return
    }
    const request = latestSuggestionRequest.current + 1
    latestSuggestionRequest.current = request
    const isLatestRequest = () => latestSuggestionRequest.current === request
    setSuggesting(true)
    setHandledSuggestions((handled) => ({ ...handled, workflowIds: [] }))
    try {
      const result = await executeAiAgent({
        accountId,
        payload: {
          type: AIAgentType.SUGGESTED_WORKFLOW,
          context: { serviceId: target.serviceId, externalId: target.externalId, roomId, sessionId },
        },
      }).unwrap()
      if (isLatestRequest()) {
        setSuggestionResult(result as SuggestedWorkflowExecutionResult)
      }
    } catch {
      if (isLatestRequest()) {
        setSuggestionResult(null)
      }
    } finally {
      if (isLatestRequest()) {
        setSuggesting(false)
      }
    }
  }, [accountId, executeAiAgent, roomId, sessionId, target])

  const isReady = status === 'ready'
  useEffect(() => {
    if (isReady) {
      runSuggestions()
    }
  }, [isReady, runSuggestions])

  const rerunSuggestions = useMemo(
    () =>
      tracked(runSuggestions, {
        eventName: TrackEvent.SUGGESTED_WORKFLOW_RERUN,
        eventDetails: { roomId, sessionId, contactId: target?.contactId, serviceId: target?.serviceId },
      }),
    [roomId, runSuggestions, sessionId, target, tracked],
  )

  const markWorkflowHandled = useCallback(
    (workflowId: number) => setHandledSuggestions((handled) => ({ ...handled, workflowIds: [...handled.workflowIds, workflowId] })),
    [],
  )
  const markTemplateHandled = useCallback(
    (templateId: string) => setHandledSuggestions((handled) => ({ ...handled, templateIds: [...handled.templateIds, templateId] })),
    [],
  )

  const suggestions = useMemo(
    () => selectSuggestions(suggestionResult?.result ?? null, lists, handledSuggestions),
    [handledSuggestions, lists, suggestionResult],
  )

  return {
    status,
    retry: refetch,
    running: lists.running,
    completed: lists.completed,
    templates: lists.templates,
    suggestions: { ...suggestions, isLoading: isSuggesting, rerun: rerunSuggestions, markWorkflowHandled, markTemplateHandled },
    counts: { suggestions: suggestions.workflows.length + suggestions.templates.length, running: lists.running.length },
  }
}
